import React from 'react';
import { Search, Download, X } from 'lucide-react';

/**
 * Shared toolbar / filter chip / pagination chrome used alongside
 * useAdminListState. AdminTable composes this internally; pages that
 * need to render their own per-row markup (e.g. PropertiesAdmin's
 * expandable cards) can compose it directly to keep visuals identical.
 */
function FilterChipGroup({ group }) {
  const { label, options, selected, onChange, multi = true } = group;
  const has = (value) => selected.includes(value);
  const toggle = (value) => {
    if (multi) {
      onChange(has(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    } else {
      onChange(has(value) ? [] : [value]);
    }
  };
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">
        {label}
      </span>
      {options.map((opt) => {
        const active = has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              active
                ? 'bg-[#043132] border-[#043132] text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            title={opt.label}
          >
            {opt.label}
            {opt.count !== undefined ? (
              <span className={`ml-1 ${active ? 'text-white/70' : 'text-slate-400'}`}>{opt.count}</span>
            ) : null}
          </button>
        );
      })}
      {selected.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
          title={`Clear ${label}`}
        >
          <X size={11} /> Clear
        </button>
      ) : null}
    </div>
  );
}

export function AdminToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filterGroups = [],
  toolbarLeft,
  toolbarRight,
  onExport,
  exportDisabled,
}) {
  return (
    <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-3 sticky top-0 z-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {toolbarLeft}
          <div className="relative flex-1 min-w-0 max-w-xl">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-9 pr-16 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none bg-white"
            />
            {search ? (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onExport ? (
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 disabled:opacity-40"
              title="Export current view as CSV"
            >
              <Download size={12} /> CSV
            </button>
          ) : null}
          {toolbarRight}
        </div>
      </div>

      {filterGroups.length > 0 ? (
        <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
          {filterGroups.map((g) => (
            <FilterChipGroup key={g.id} group={g} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AdminPagination({
  page,
  setPage,
  pageCount,
  pageSize,
  setPageSize,
  total,
  rawTotal,
  showingFrom,
  showingTo,
  pageSizeOptions = [25, 50, 100, 'all'],
}) {
  const isAllPage = pageSize === 'all';
  if (total === 0) return null;
  return (
    <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
      <div>
        Showing {showingFrom}–{showingTo} of {total}
        {rawTotal != null && rawTotal !== total ? (
          <span className="text-slate-400"> · {rawTotal} total before filtering</span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const v = e.target.value;
              setPageSize(v === 'all' ? 'all' : Number(v));
            }}
            className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt === 'all' ? 'All' : opt}</option>
            ))}
          </select>
        </label>
        {!isAllPage ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(Math.max(1, page - 1))}
              className="px-2 py-1 rounded border border-slate-200 hover:border-slate-300 disabled:opacity-40"
            >
              Prev
            </button>
            <span className="px-2">Page {page} / {pageCount}</span>
            <button
              type="button"
              disabled={page >= pageCount}
              onClick={() => setPage(Math.min(pageCount, page + 1))}
              className="px-2 py-1 rounded border border-slate-200 hover:border-slate-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
